/**
 * AI Service - Gemini API Integration
 *
 * This service handles:
 * 1. AI Health Analysis Report Generation (based on menstrual & exercise data)
 * 2. AI Health Chat Interface (ask questions about health)
 *
 * Provider: Gemini 1.5 Pro (with OpenAI GPT-4 as backup)
 * Version: 3.2
 * Date: 2025-10-17
 * Author: Yufan
 */

import { GoogleGenerativeAI } from '@google/generative-ai'

class AIService {
  constructor() {
    // Configure AI provider (Gemini or OpenAI)
    this.provider = process.env.VUE_APP_GENAI_PROVIDER || 'gemini'
    this.geminiApiKey = process.env.VUE_APP_GEMINI_API_KEY || ''
    this.openaiApiKey = process.env.VUE_APP_OPENAI_API_KEY || ''

    // Initialize Gemini AI
    if (this.provider === 'gemini' && this.geminiApiKey) {
      this.genAI = new GoogleGenerativeAI(this.geminiApiKey)
      // Use Gemini 2.5 Flash - best price-performance model
      this.model = this.genAI.getGenerativeModel({ model: 'gemini-2.5-flash' })
    }
  }

  /**
   * Generate AI Health Analysis Report
   * Based on menstrual cycle and exercise data
   *
   * @param {Object} userData - User profile and health data
   * @returns {Promise<Object>} AI-generated health report
   */
  async generateHealthReport(userData) {
    try {
      const prompt = this._buildHealthReportPrompt(userData)

      if (this.provider === 'gemini') {
        const result = await this.model.generateContent(prompt)
        const response = await result.response
        const text = response.text()

        // Parse AI response into structured format
        return this._parseHealthReport(text)
      } else if (this.provider === 'openai') {
        // TODO: Implement OpenAI integration
        throw new Error('OpenAI provider not yet implemented')
      }

      throw new Error('Invalid AI provider configured')
    } catch (error) {
      console.error('Error generating health report:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Ask AI a health-related question
   *
   * @param {string} question - User's health question
   * @param {Object} context - User's health context (phase, exercise, etc.)
   * @returns {Promise<Object>} AI response
   */
  async askHealthQuestion(question, context) {
    try {
      const prompt = this._buildChatPrompt(question, context)

      if (this.provider === 'gemini') {
        const result = await this.model.generateContent(prompt)
        const response = await result.response
        const text = response.text()

        return {
          success: true,
          answer: text.trim()
        }
      } else if (this.provider === 'openai') {
        // TODO: Implement OpenAI integration
        throw new Error('OpenAI provider not yet implemented')
      }

      throw new Error('Invalid AI provider configured')
    } catch (error) {
      console.error('Error asking health question:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Build prompt for health report generation
   * @private
   */
  _buildHealthReportPrompt(userData) {
    const { userProfile, menstrualCycle, exerciseData } = userData

    return `You are a professional women's health AI assistant. Generate a personalized health analysis report based on the following data:

USER PROFILE:
- Age: ${userProfile.age || 'Not provided'}
- Gender: ${userProfile.gender || 'female'}

MENSTRUAL CYCLE:
- Current Phase: ${menstrualCycle.phase || 'Unknown'}
- Average Cycle Length: ${menstrualCycle.averageCycleLength || 28} days
- Period Start Date: ${menstrualCycle.periodStartDate || 'Not provided'}

RECENT EXERCISE DATA (Last 7 days):
${exerciseData && exerciseData.length > 0
  ? exerciseData.map(e => `- ${e.date}: ${e.activity} for ${e.duration_min} min (${e.intensity} intensity)`).join('\n')
  : '- No recent exercise data available'}

TASK:
Generate a comprehensive health analysis report in JSON format with the following structure:

{
  "phase_analysis": "Detailed explanation of current menstrual phase and what to expect",
  "exercise_recommendation": ["Recommendation 1", "Recommendation 2", "Recommendation 3"],
  "energy_recovery": ["Energy tip 1", "Recovery tip 2"],
  "nutrition_tips": ["Nutrition tip 1", "Nutrition tip 2", "Nutrition tip 3"],
  "summary": "Overall summary and encouragement",
  "health_score": 85
}

GUIDELINES:
- Use friendly, supportive, and actionable language
- Focus on the menstrual phase impact on exercise and energy
- Provide specific, practical recommendations
- Health score should be 1-100 based on exercise consistency and phase awareness
- Keep each recommendation concise (1-2 sentences)
- Be encouraging and positive

Return ONLY the JSON object, no additional text.`
  }

  /**
   * Build prompt for chat question
   * @private
   */
  _buildChatPrompt(question, context) {
    const { phase, recentExercise } = context

    return `You are a professional women's health AI assistant specializing in menstrual health and fitness.

CONTEXT:
- Current Menstrual Phase: ${phase || 'Unknown'}
- Recent Exercise: ${recentExercise && recentExercise.length > 0 ? recentExercise.join(', ') : 'None recorded'}

USER QUESTION:
${question}

GUIDELINES:
- Provide a clear, helpful, and friendly answer
- Keep responses concise (2-4 sentences)
- Focus on the relationship between menstrual cycle and exercise/health
- Use simple, non-medical language
- Be supportive and encouraging

YOUR ANSWER:`
  }

  /**
   * Parse AI-generated health report text into structured format
   * @private
   */
  _parseHealthReport(text) {
    try {
      // Try to extract JSON from the response
      const jsonMatch = text.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        const parsedReport = JSON.parse(jsonMatch[0])
        return {
          success: true,
          report: parsedReport
        }
      }

      // If no JSON found, return error
      return {
        success: false,
        error: 'Could not parse AI response',
        rawText: text
      }
    } catch (error) {
      console.error('Error parsing health report:', error)
      return {
        success: false,
        error: 'Failed to parse AI response',
        rawText: text
      }
    }
  }

  /**
   * Mock health report for testing (when API is not available)
   */
  async generateMockHealthReport(userData) {
    const { menstrualCycle } = userData
    const phase = menstrualCycle?.phase || 'Unknown'

    return {
      success: true,
      report: {
        phase_analysis: `You are currently in the ${phase} phase. ${this._getPhaseDescription(phase)}`,
        exercise_recommendation: [
          'Focus on moderate-intensity activities like yoga or walking',
          'Listen to your body and rest when needed',
          'Stay hydrated during and after workouts'
        ],
        energy_recovery: [
          'Prioritize 7-8 hours of quality sleep each night',
          'Take short breaks throughout the day to recharge',
          'Consider gentle stretching or meditation for recovery'
        ],
        nutrition_tips: [
          'Eat iron-rich foods like spinach, lean meat, and beans',
          'Include complex carbohydrates for sustained energy',
          'Stay well-hydrated with plenty of water'
        ],
        summary: "You're maintaining good health awareness. Keep listening to your body and adjusting your routine as needed.",
        health_score: 80
      }
    }
  }

  /**
   * Get phase description helper
   * @private
   */
  _getPhaseDescription(phase) {
    const descriptions = {
      'Menstrual': 'Your body is shedding the uterine lining. Energy levels may be lower, so gentle movement is recommended.',
      'Follicular': 'Your energy is building up! This is a great time for more intense workouts and trying new activities.',
      'Ovulation': 'You may feel your strongest now. Take advantage of high energy for challenging workouts.',
      'Luteal': 'Your body temperature may increase slightly. Focus on moderate activities and prioritize recovery.',
      'Unknown': 'Start tracking your cycle to get personalized insights!'
    }
    return descriptions[phase] || descriptions['Unknown']
  }
}

export default new AIService()
